import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, SimpleGrid, Card, CardBody, CardHeader,
  Spinner, Flex, Table, Thead, Tbody, Tr, Th, Td, Badge, Progress,
  VStack, Switch, FormControl, FormLabel, Button,
} from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const statusColor = (s) => {
  if (s === 'completed') return 'green';
  if (s === 'failed' || s === 'error') return 'red';
  if (s === 'running') return 'blue';
  if (s === 'pending') return 'yellow';
  return 'gray';
};

const capabilityLabel = (cap) => {
  if (!cap) return 'Unknown';
  if (cap === 'mcp-call') return 'MCP Call';
  return cap.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const AdminAgentActivity = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [displayAllUsers, setDisplayAllUsers] = useState(false);

  useEffect(() => {
    setLoading(true);
    setDisplayAllUsers(false);
    fetchWithAuth(`/api/admin/agent-tasks/activity?include_admins=${includeAdmins}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [includeAdmins]);

  if (loading) return <Box textAlign="center" py={10}><Spinner size="lg" /></Box>;
  if (!data) return <Box py={6}><Text color="gray.400" fontSize="sm">Failed to load agent activity data.</Text></Box>;

  const total = data.by_status.reduce((sum, s) => sum + s.count, 0);
  const completed = data.by_status.find(s => s.status === 'completed')?.count || 0;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;
  const thisWeek = data.volume_trend.slice(-7).reduce((sum, d) => sum + d.count, 0);
  const topCap = data.by_capability[0];

  const maxTrend = Math.max(...data.volume_trend.map(d => d.count), 1);
  const maxCap = Math.max(...data.by_capability.map(d => d.count), 1);
  const visibleUsers = displayAllUsers ? data.top_users : data.top_users.slice(0, 10);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Agent Activity</Heading>
        <FormControl display="flex" alignItems="center" w="auto">
          <FormLabel htmlFor="include-admins" mb={0} fontSize="sm" color="gray.600" mr={2}>
            Include admins
          </FormLabel>
          <Switch
            id="include-admins"
            size="sm"
            isChecked={includeAdmins}
            onChange={e => setIncludeAdmins(e.target.checked)}
            colorScheme="purple"
          />
        </FormControl>
      </Flex>

      {/* Summary stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        {[
          { label: 'Total Tasks', value: total, sub: 'All time', color: 'gray' },
          { label: 'This Week', value: thisWeek, sub: 'Last 7 days', color: 'blue' },
          { label: 'Completion Rate', value: `${completionRate}%`, sub: `${completed} completed`, color: completionRate >= 70 ? 'green' : completionRate >= 40 ? 'orange' : 'red' },
          { label: 'Top Capability', value: topCap ? capabilityLabel(topCap.capability).split(' ').slice(0, 2).join(' ') : '—', sub: topCap ? `${topCap.count} tasks` : '', color: 'purple' },
        ].map(({ label, value, sub, color }) => (
          <Card key={label} variant="outline">
            <CardBody p={4} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" color={`${color}.500`}>{value}</Text>
              <Text fontSize="xs" fontWeight="medium" color="gray.600">{label}</Text>
              <Text fontSize="xs" color="gray.400">{sub}</Text>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
        {/* Volume trend */}
        <Card variant="outline">
          <CardHeader pb={2}><Heading size="sm">Volume (Last 14 Days)</Heading></CardHeader>
          <CardBody pt={2}>
            {data.volume_trend.length === 0 ? (
              <Text fontSize="sm" color="gray.400">No data yet</Text>
            ) : (
              <VStack spacing={1} align="stretch">
                {data.volume_trend.map((d, i) => (
                  <Flex key={i} align="center" gap={3}>
                    <Text fontSize="xs" color="gray.500" w="56px">
                      {new Date(d.day).toLocaleDateString('en-NZ', { month: 'short', day: 'numeric' })}
                    </Text>
                    <Box flex={1}>
                      <Progress value={(d.count / maxTrend) * 100} size="sm" colorScheme="blue" borderRadius="full" />
                    </Box>
                    <Text fontSize="xs" fontWeight="600" w="24px" textAlign="right">{d.count}</Text>
                  </Flex>
                ))}
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* Status breakdown */}
        <Card variant="outline">
          <CardHeader pb={2}><Heading size="sm">By Status</Heading></CardHeader>
          <CardBody pt={2}>
            <Table size="sm">
              <Tbody>
                {data.by_status.map(row => (
                  <Tr key={row.status}>
                    <Td><Badge colorScheme={statusColor(row.status)}>{row.status}</Badge></Td>
                    <Td isNumeric fontWeight="600">{row.count}</Td>
                    <Td isNumeric>
                      <Text fontSize="xs" color="gray.400">{total > 0 ? Math.round((row.count / total) * 100) : 0}%</Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Capability breakdown */}
      <Card variant="outline" mb={6}>
        <CardHeader pb={2}><Heading size="sm">Capability Breakdown</Heading></CardHeader>
        <CardBody pt={2}>
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Capability</Th>
                <Th isNumeric>Total</Th>
                <Th isNumeric>Completed</Th>
                <Th isNumeric>Failed</Th>
                <Th>Usage</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.by_capability.map(row => (
                <Tr key={row.capability}>
                  <Td><Text fontSize="sm">{capabilityLabel(row.capability)}</Text></Td>
                  <Td isNumeric fontWeight="600">{row.count}</Td>
                  <Td isNumeric><Text color="green.600">{row.completed}</Text></Td>
                  <Td isNumeric><Text color={row.failed > 0 ? 'red.500' : 'gray.400'}>{row.failed}</Text></Td>
                  <Td w="120px">
                    <Progress value={(row.count / maxCap) * 100} size="xs" colorScheme="blue" borderRadius="full" />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Top users */}
        <Card variant="outline">
          <CardHeader pb={2}><Heading size="sm">Most Active Users</Heading></CardHeader>
          <CardBody pt={2}>
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>User</Th>
                  <Th isNumeric>Tasks</Th>
                  <Th isNumeric>Done</Th>
                </Tr>
              </Thead>
              <Tbody>
                {visibleUsers.map(u => (
                  <Tr key={u.user_id}>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium">{u.name}</Text>
                      <Text fontSize="xs" color="gray.400" mb={1}>{u.email}</Text>
                      <Flex gap={1} flexWrap="wrap">
                        {(u.capabilities || []).map(c => (
                          <Badge key={c.capability} fontSize="9px" colorScheme="purple" variant="subtle" borderRadius="full">
                            {capabilityLabel(c.capability)} ×{c.count}
                          </Badge>
                        ))}
                      </Flex>
                    </Td>
                    <Td isNumeric fontWeight="600">{u.task_count}</Td>
                    <Td isNumeric><Text color="green.600">{u.completed_count}</Text></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {data.top_users.length > 10 && (
              <Flex justify="center" pt={4}>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  onClick={() => setDisplayAllUsers(value => !value)}
                >
                  {displayAllUsers ? 'Show less' : 'Display all'}
                </Button>
              </Flex>
            )}
          </CardBody>
        </Card>

        {/* Top plans */}
        <Card variant="outline">
          <CardHeader pb={2}><Heading size="sm">Most Active Plans</Heading></CardHeader>
          <CardBody pt={2}>
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Plan</Th>
                  <Th isNumeric>Tasks</Th>
                  <Th isNumeric>Done</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.top_plans.map(p => (
                  <Tr key={p.plan_id}>
                    <Td>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                        {p.name}
                      </Text>
                      <Text fontSize="xs" color="gray.400">#{p.plan_id}</Text>
                    </Td>
                    <Td isNumeric fontWeight="600">{p.task_count}</Td>
                    <Td isNumeric><Text color="green.600">{p.completed_count}</Text></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </SimpleGrid>
    </Box>
  );
};

export default AdminAgentActivity;
